import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faClipboardList, faSpinner, faSearch,
    faFilter, faEye, faClock,
    faCheckCircle, faTimesCircle, faUser,
    faCalendar, faChevronRight, faPlus,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons'
import { fetchPurchaseRequests } from './purchaseRequestService'
import PageHeader from '../utils/PageHeader'
import '../../css/purchase.css'
import '../../css/common.css'

/* ─── Status config ────────────────────────────────────────── */
const STATUS_CONFIG = {
    pending: {
        label: 'Pending',
        cls: 'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-orange-50 border border-orange-200 text-orange-700 whitespace-nowrap',
        icon: faClock,
    },
    approved: {
        label: 'Approved',
        cls: 'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-50 border border-green-200 text-green-700 whitespace-nowrap',
        icon: faCheckCircle,
    },
    rejected: {
        label: 'Rejected',
        cls: 'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-50 border border-red-200 text-red-700 whitespace-nowrap',
        icon: faTimesCircle,
    },
}

const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status?.toLowerCase()] ?? STATUS_CONFIG.pending
    return (
        <span className={cfg.cls}>
            <FontAwesomeIcon icon={cfg.icon} className='text-xs' />
            {cfg.label}
        </span>
    )
}

const OemBadge = ({ isOem }) => (
    <span
        className={
            isOem
                ? 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-purple-50 border border-purple-200 text-purple-700 whitespace-nowrap'
                : 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-50 border border-gray-200 text-gray-600 whitespace-nowrap'
        }
    >
        {isOem ? 'OEM' : 'Non-OEM'}
    </span>
)

const formatDate = (d) =>
    d
        ? new Date(d).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
        })
        : '—'

/* ══════════════════════════════════════════
   ViewPR — Purchase Request List
══════════════════════════════════════════ */
const ViewPR = () => {
    const token = useSelector((store) => store.user?.token) || localStorage.getItem('token')
    const navigate = useNavigate()

    const [prList, setPRList] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [search, setSearch] = useState('')
    const [statusFilter, setFilter] = useState('all')
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [totalDocs, setTotalDocs] = useState(0)

    const LIMIT = 15

    /* ── Fetch from API ── */
    const fetchPRs = useCallback(
        async (pg = 1) => {
            setLoading(true)
            setError('')
            try {
                const data = await fetchPurchaseRequests(token, {
                    search: search.trim() || undefined,
                    approvalStatus: statusFilter !== 'all' ? statusFilter : undefined,
                    page: pg,
                    limit: LIMIT,
                    sortBy: 'createdAt',
                    sortOrder: 'desc',
                })
                const payload = data?.data ?? data
                const docs = payload?.docs ?? payload?.purchaseRequests ?? (Array.isArray(payload) ? payload : [])
                const pages = payload?.totalPages ?? payload?.pages ?? 1
                const total = payload?.totalDocs ?? payload?.total ?? docs.length

                setPRList(Array.isArray(docs) ? docs : [])
                setTotalPages(pages)
                setTotalDocs(total)
                setPage(pg)
            } catch (e) {
                console.error(e)
                setError(e.message || 'Failed to load purchase requests.')
                setPRList([])
            } finally {
                setLoading(false)
            }
        },
        [token, search, statusFilter]
    )

    /* Initial load */
    useEffect(() => { fetchPRs(1) }, []) // eslint-disable-line react-hooks/exhaustive-deps

    /* Re-fetch when status filter changes */
    useEffect(() => { fetchPRs(1) }, [statusFilter]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = (e) => { e.preventDefault(); fetchPRs(1) }

    return (
        <div>
            <PageHeader
                pageName='VIEW PURCHASE REQUESTS'
                breadcrump1='Home'
                breadcrump2='View Purchase Requests'
                link='/dashboard'
            />

            <div className='main lg:p-6 xl:p-6 2xl:p-6 sm:p-3 md:p-3'>

                {/* ── Top action ── */}
                <div className='flex justify-end mb-4'>
                    <button
                        id='btn-new-pr'
                        className='btn add-btn text-white flex items-center gap-2 font-bold text-sm'
                        onClick={() => navigate('/purchase_request')}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        New Purchase Request
                    </button>
                </div>

                {/* ── Filter bar ── */}
                <div className='bg-white border border-gray-200 rounded-xl p-4 mb-5 shadow-sm'>
                    <div className='flex items-center gap-3 flex-wrap'>
                        {/* Search */}
                        <form onSubmit={handleSearch} className='flex-1 min-w-56 relative'>
                            <FontAwesomeIcon
                                icon={faSearch}
                                className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none'
                            />
                            <input
                                id='pr-search-input'
                                type='text'
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder='Search by PR ID or raised by name…'
                                className='purchase-search-input'
                            />
                        </form>

                        {/* Status dropdown */}
                        <div className='flex items-center gap-2'>
                            <FontAwesomeIcon icon={faFilter} className='text-gray-400 text-sm' />
                            <select
                                id='pr-status-filter'
                                className='form-select form-select-sm'
                                value={statusFilter}
                                onChange={(e) => setFilter(e.target.value)}
                            >
                                <option value='all'>All Statuses</option>
                                <option value='pending'>Pending</option>
                                <option value='approved'>Approved</option>
                                <option value='rejected'>Rejected</option>
                            </select>
                        </div>

                        <button
                            id='pr-apply-filter'
                            className='btn filter-btn text-white btn-sm px-4'
                            onClick={() => fetchPRs(1)}
                        >
                            Apply
                        </button>
                    </div>
                </div>

                {/* ── Error banner ── */}
                {error && (
                    <div className='flex items-center gap-2 rounded-lg px-4 py-3 mb-4 bg-red-50 border border-red-200 text-red-700 text-sm'>
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        {error}
                    </div>
                )}

                {/* ── Table card ── */}
                <div className='bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden'>
                    <div className='px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-2'>
                        <div className='flex items-center gap-2'>
                            <FontAwesomeIcon icon={faClipboardList} className='text-purple-600' />
                            <h6 className='font-bold text-gray-800 mb-0'>Purchase Requests</h6>
                        </div>
                        {!loading && totalDocs > 0 && (
                            <span className='text-xs text-gray-500 font-semibold'>
                                {totalDocs} record{totalDocs !== 1 ? 's' : ''}
                            </span>
                        )}
                    </div>

                    {/* Loading */}
                    {loading ? (
                        <div className='text-center py-14 text-gray-400'>
                            <FontAwesomeIcon icon={faSpinner} spin className='text-3xl mb-3' />
                            <p className='text-sm'>Loading purchase requests…</p>
                        </div>
                    ) : prList.length === 0 ? (
                        <div className='text-center py-14 text-gray-400'>
                            <FontAwesomeIcon
                                icon={faClipboardList}
                                className='text-4xl mb-3 text-gray-300'
                            />
                            <p className='text-sm font-semibold text-gray-500'>
                                No Purchase Requests Found
                            </p>
                            <p className='text-xs text-gray-400'>
                                No PRs match your current filters.
                            </p>
                        </div>
                    ) : (
                        <div className='table-responsive'>
                            <table className='table align-middle table-nowrap mb-0 purchase-table'>
                                <thead className='table-light'>
                                    <tr>
                                        <th>PR ID</th>
                                        <th>Request Date</th>
                                        <th>Raised By</th>
                                        <th>Sales Order</th>
                                        <th>Client</th>
                                        <th className='text-center'>Items</th>
                                        <th>Type</th>
                                        <th>Approval Status</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {prList.map((pr, idx) => (
                                        <tr key={pr._id || idx}>
                                            {/* PR ID */}
                                            <td>
                                                <span className='font-bold text-purple-700 text-sm'>
                                                    {pr.prId || '—'}
                                                </span>
                                            </td>

                                            {/* Date */}
                                            <td>
                                                <div className='flex items-center gap-1 text-xs text-gray-500'>
                                                    <FontAwesomeIcon
                                                        icon={faCalendar}
                                                        className='text-gray-400'
                                                    />
                                                    {formatDate(pr.date || pr.createdAt)}
                                                </div>
                                            </td>

                                            {/* Raised By */}
                                            <td>
                                                <div className='flex items-center gap-2'>
                                                    <div className='w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0'>
                                                        <FontAwesomeIcon
                                                            icon={faUser}
                                                            className='text-purple-600 text-xs'
                                                        />
                                                    </div>
                                                    <span className='text-sm'>
                                                        {pr.requestRaisedBy ||
                                                            pr.createdBy?.name ||
                                                            '—'}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Sales Order */}
                                            <td className='text-sm font-semibold text-gray-700'>
                                                {pr.salesOrder?.salesOrderNumber ||
                                                    (typeof pr.salesOrder === 'string'
                                                        ? pr.salesOrder
                                                        : '—')}
                                            </td>

                                            <td className='text-sm font-semibold text-gray-700'>
                                                {pr.salesOrder?.client?.companyName ||
                                                    (typeof pr.salesOrder === 'string'
                                                        ? pr.salesOrder
                                                        : '—')}
                                            </td>

                                            {/* Items */}
                                            <td className='text-center font-bold text-gray-700'>
                                                {Array.isArray(pr.lineItems)
                                                    ? pr.lineItems.length
                                                    : '—'}
                                            </td>

                                            {/* OEM / Non-OEM */}
                                            <td>
                                                <OemBadge isOem={!!pr.isOem} />
                                            </td>

                                            {/* Status */}
                                            <td>
                                                <StatusBadge status={pr.approvalStatus} />
                                            </td>

                                            {/* Action */}
                                            <td>
                                                <button
                                                    id={`btn-view-pr-${pr._id || idx}`}
                                                    className='btn btn-sm filter-btn text-white flex items-center gap-1'
                                                    onClick={() => navigate(`/view_pr/${pr._id}`)}
                                                >
                                                    <FontAwesomeIcon icon={faEye} /> View
                                                    <FontAwesomeIcon
                                                        icon={faChevronRight}
                                                        className='text-xs'
                                                    />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* ── Pagination ── */}
                    {totalPages > 1 && (
                        <div className='px-5 py-3 border-t border-gray-100 flex justify-end gap-2'>
                            <button
                                className='btn btn-sm btn-light border'
                                disabled={page <= 1}
                                onClick={() => fetchPRs(page - 1)}
                            >
                                « Prev
                            </button>
                            <span className='flex items-center text-sm text-gray-500 px-3'>
                                Page {page} of {totalPages}
                            </span>
                            <button
                                className='btn btn-sm btn-light border'
                                disabled={page >= totalPages}
                                onClick={() => fetchPRs(page + 1)}
                            >
                                Next »
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default ViewPR
